import Prism from 'prismjs';
import { useEffect, useRef, useState } from 'react';

import { loadTxt } from '../utils/loadTxt';
import { mergeClasses } from '../utils/mergeClasses';
import { CopyToClipboard } from './CopyToClipboard';

type Props = {
  code?: string;
  language?: string;
  dataSrc?: string;
  showClipboard?: boolean;
  title?: string;
  withRadius?: boolean;
  size?: 'small' | 'large';
};

/**
 * Purpose: Highlights code snippets.
 *
 * Usage:
 * <PrismCode code={'console.log('hello')'} />
 * <PrismCode dataSrc={'file-in-public-folder'} />
 *
 * dataSrc will be the only one taken into account if both dataSrc & code are passed.
 */
export const PrismCode = ({
  code,
  language,
  dataSrc,
  showClipboard,
  title,
  withRadius = true,
  size
}: Props) => {
  const codeRef = useRef<HTMLElement | null>(null);
  const [sourceCode, setSourceCode] = useState(code);

  useEffect(() => {
    const loadCode = async () => {
      if (dataSrc) {
        const loadedCode = await loadTxt(dataSrc);
        if (loadedCode) {
          setSourceCode(loadedCode);
        }
      } else {
        setSourceCode(code);
      }
    };

    loadCode();
  }, [dataSrc, code]);

  useEffect(() => {
    if (codeRef.current) {
      Prism.highlightElement(codeRef.current);
    }
  }, [sourceCode]);

  return (
    <div
      data-testid="prism-code-snippet"
      className={mergeClasses(
        'flex flex-col bg-inpay-dark-green-1000 text-white',
        withRadius ? 'rounded-md' : ''
      )}
    >
      {title && (
        <div className="flex justify-between rounded-t-xl bg-inpay-green-primary-mixes-20%-black p-4 text-lg">
          <h2>{title}</h2>
          {sourceCode && <CopyToClipboard value={sourceCode} />}
        </div>
      )}
      <div className="relative w-full">
        {showClipboard && sourceCode && !title && (
          <CopyToClipboard value={sourceCode} absolute />
        )}
        <pre
          className={mergeClasses(
            !title ? 'rounded-t-xl' : '',
            size === 'small' ? 'h-[300px] overflow-auto' : 'overflow-hidden',
            'rounded-b-xl bg-inpay-dark-green-1000 p-2 text-white hover:overflow-auto'
          )}
        >
          <code className={`language-${language}`} ref={codeRef}>
            {sourceCode}
          </code>
        </pre>
      </div>
    </div>
  );
};
